/**
 * Contractor Mobile Job Detail - Field Execution Screen
 * 
 * DESIGN PRINCIPLE: Uber-driver-app-style UX
 * - Large touch targets (min 48px)
 * - Sticky bottom action buttons
 * - Status pills
 * - Photo upload preview
 * - Loading indicators
 * - Submission states
 * - Clear error messages
 * 
 * VALIDATION: Cannot complete unless required items uploaded
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import * as Sentry from '@sentry/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helpdesk contact info
const HELPDESK = {
  phone: '07557017694',
  email: 'advice@parkhomesupport.co.uk',
};

// ============================================================
// TOKEN MANAGEMENT
// ============================================================
const getToken = (searchParams) => {
  const urlToken = searchParams.get('token');
  if (urlToken) {
    localStorage.setItem('contractor_mobile_token', urlToken);
    return urlToken;
  }
  return localStorage.getItem('contractor_mobile_token') || 
         localStorage.getItem('contractor_portal_token');
};

// ============================================================
// STATUS UTILITIES
// ============================================================
const getStatusConfig = (status) => {
  const statusMap = {
    'Assigned': { label: 'Assigned', bg: 'bg-blue-500', actions: ['accept', 'decline'] },
    'Scheduled': { label: 'Scheduled', bg: 'bg-cyan-500', actions: ['start', 'reschedule'] },
    'In Progress': { label: 'In Progress', bg: 'bg-amber-500', actions: ['complete'] },
    'Work Completed': { label: 'Completed', bg: 'bg-emerald-500', actions: ['jobsheet'] },
    'Approved': { label: 'Ready to Start', bg: 'bg-green-500', actions: ['start'] },
    'Awaiting Quote': { label: 'Quote Needed', bg: 'bg-orange-500', actions: ['quote'] },
    'Quote Submitted': { label: 'Quote Sent', bg: 'bg-purple-500', actions: [] },
  };
  return statusMap[status] || { label: status || 'Unknown', bg: 'bg-slate-400', actions: [] };
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function ContractorMobileJobPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const jobSheetInputRef = useRef(null);
  const signatureCanvasRef = useRef(null);

  // State
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [jobSheet, setJobSheet] = useState(null);
  const [completionNote, setCompletionNote] = useState('');
  const [signature, setSignature] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [signerName, setSignerName] = useState('');

  // Quote state - enhanced with full breakdown
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteLabour, setQuoteLabour] = useState('');
  const [quoteLabourDesc, setQuoteLabourDesc] = useState('');
  const [quoteMaterials, setQuoteMaterials] = useState('');
  const [quoteMaterialsDesc, setQuoteMaterialsDesc] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteStartDate, setQuoteStartDate] = useState('');
  const [quoteDuration, setQuoteDuration] = useState('');
  const [quoteFile, setQuoteFile] = useState(null);
  const [loadError, setLoadError] = useState(null);
  
  // Auto-calculate totals
  const labourNum = parseFloat(quoteLabour) || 0;
  const materialsNum = parseFloat(quoteMaterials) || 0;
  const subtotal = labourNum + materialsNum;
  const vatAmount = subtotal * 0.2;
  const total = subtotal + vatAmount;

  // Token management
  const token = getToken(searchParams);

  const api = axios.create({ 
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // ============================================================
  // LOAD JOB - with retry and better error handling
  // ============================================================
  const loadJob = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setLoadError(null);
    try {
      // Try mobile API first
      let res;
      try {
        res = await api.get(`/mobile/jobs/${jobId}`);
        if (res.data.job) {
          setJob(res.data.job);
          if (res.data.photos) setPhotos(res.data.photos.map(p => ({ ...p, uploaded: true })));
          return;
        }
      } catch (mobileErr) {
        console.log('Mobile API error, trying contractor API');
      }

      // Fallback: contractor-company API
      try {
        res = await api.get(`/contractor-company/jobs/${jobId}`);
        setJob(res.data.data || res.data);
        return;
      } catch (contractorErr) {
        console.log('Contractor API error, trying token-based API');
      }

      // Fallback: token-based contractor job
      if (token) {
        res = await axios.get(`${API_URL}/api/contractor/job/${jobId}?token=${encodeURIComponent(token)}`);
        const data = res.data.data || res.data;
        setJob(data.job || data);
        return;
      }

      throw new Error('No valid API found');
    } catch (err) {
      console.error('Load error:', err);
      
      // Retry up to 3 times with delay
      if (retryCount < 2) {
        console.log(`Retrying load job (attempt ${retryCount + 2})...`);
        setTimeout(() => loadJob(retryCount + 1), 1500);
        return;
      }
      
      // Log to Sentry with context
      Sentry.captureException(err, {
        tags: {
          component: 'ContractorMobileJobPage',
          action: 'loadJob',
        },
        extra: {
          jobId,
          hasToken: !!token,
          retryCount,
          apiStatus: err.response?.status,
          userAgent: navigator.userAgent,
        }
      });
      
      // Set error state for recoverable UI
      const isExpiredToken = err.response?.status === 401 || err.response?.status === 403;
      const isNotFound = err.response?.status === 404;
      
      if (isExpiredToken) {
        setLoadError({
          type: 'expired',
          message: 'This job link has expired or is invalid.',
          detail: 'Please contact the helpdesk for a new link.'
        });
      } else if (isNotFound) {
        setLoadError({
          type: 'notfound',
          message: 'Job not found.',
          detail: 'This job may have been cancelled or the link is incorrect.'
        });
      } else {
        setLoadError({
          type: 'error',
          message: 'Unable to load job.',
          detail: 'Please check your connection and try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, token]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // ============================================================
  // ACTIONS
  // ============================================================
  
  // Accept Job
  const handleAccept = async () => {
    setActionLoading('accept');
    try {
      // Try multiple endpoints
      try {
        await api.post(`/mobile/jobs/${jobId}/status`, { status: 'Accepted' });
      } catch {
        try {
          await api.post(`/contractor-company/jobs/${jobId}/accept`);
        } catch {
          await axios.post(`${API_URL}/api/contractor/job/${jobId}/respond?token=${encodeURIComponent(token)}`, { action: 'accept' });
        }
      }
      toast.success('Job accepted!');
      loadJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    } finally {
      setActionLoading(null);
    }
  };

  // Decline Job
  const handleDecline = async () => {
    const reason = prompt('Please enter reason for declining:');
    if (!reason) return;
    
    setActionLoading('decline');
    try {
      await axios.post(`${API_URL}/api/contractor/job/${jobId}/respond?token=${encodeURIComponent(token)}`, { 
        action: 'decline',
        decline_reason: reason 
      });
      toast.success('Job declined');
      navigate('/contractor/mobile');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    } finally {
      setActionLoading(null);
    }
  };

  // Start Work
  const handleStart = async () => {
    setActionLoading('start');
    try {
      try {
        await api.post(`/mobile/jobs/${jobId}/status`, { status: 'In Progress' });
      } catch {
        try {
          await api.post(`/contractor-company/jobs/${jobId}/start`);
        } catch {
          await axios.post(`${API_URL}/api/contractor/job/${jobId}/confirm-attendance?token=${encodeURIComponent(token)}`);
        }
      }
      toast.success('Work started!');
      loadJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    } finally {
      setActionLoading(null);
    }
  };

  // Complete Work - WITH VALIDATION
  const handleComplete = async () => {
    // Validation
    const errors = [];
    if (!completionNote.trim()) errors.push('Completion note is required');
    if (photos.length === 0) errors.push('At least 1 photo is required');
    
    if (errors.length > 0) {
      toast.error(errors.join('. '));
      return;
    }

    setActionLoading('complete');
    try {
      // Upload photos first if any local ones
      const localPhotos = photos.filter(p => !p.uploaded);
      if (localPhotos.length > 0) {
        const formData = new FormData();
        localPhotos.forEach(photo => {
          if (photo.file) formData.append('file', photo.file);
        });
        formData.append('photo_type', 'completion');
        formData.append('caption', completionNote);
        
        try {
          await api.post(`/mobile/jobs/${jobId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch {
          // Fallback upload
          await axios.post(`${API_URL}/api/contractor/job/${jobId}/upload?token=${encodeURIComponent(token)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      // Upload job sheet if exists
      if (jobSheet && !jobSheet.uploaded) {
        const formData = new FormData();
        formData.append('file', jobSheet);
        formData.append('file_type', 'job_sheet');
        
        try {
          await axios.post(`${API_URL}/api/contractor/job/${jobId}/upload?token=${encodeURIComponent(token)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadErr) {
          console.log('Job sheet upload failed:', uploadErr);
        }
      }

      // Complete the job
      try {
        await api.post(`/mobile/jobs/${jobId}/status`, { 
          status: 'Work Completed',
          note: completionNote 
        });
      } catch {
        try {
          await api.post(`/contractor-company/jobs/${jobId}/complete`, { notes: completionNote });
        } catch {
          await axios.post(`${API_URL}/api/contractor/job/${jobId}/complete?token=${encodeURIComponent(token)}`);
        }
      }

      toast.success('Job completed!', { icon: '🎉' });
      loadJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    } finally {
      setActionLoading(null);
    }
  };

  // Submit Quote - enhanced with full breakdown
  const handleSubmitQuote = async () => {
    if (subtotal <= 0) {
      toast.error('Enter labour or materials cost');
      return;
    }

    setActionLoading('quote');
    try {
      const lineItems = [];
      if (labourNum > 0) {
        lineItems.push({ 
          description: quoteLabourDesc || 'Labour', 
          quantity: 1, 
          unit_cost: labourNum, 
          line_total: labourNum 
        });
      }
      if (materialsNum > 0) {
        lineItems.push({ 
          description: quoteMaterialsDesc || 'Materials', 
          quantity: 1, 
          unit_cost: materialsNum, 
          line_total: materialsNum 
        });
      }
      
      const quoteData = {
        scope_of_works: quoteNotes || 'As per job description',
        line_items: lineItems,
        labour_description: quoteLabourDesc,
        labour_cost: labourNum,
        materials_description: quoteMaterialsDesc,
        materials_cost: materialsNum,
        subtotal_ex_vat: subtotal,
        vat_amount: vatAmount,
        total_inc_vat: total,
        estimated_start_date: quoteStartDate || null,
        estimated_duration: quoteDuration || null,
      };
      
      // Upload quote file if provided
      if (quoteFile) {
        const formData = new FormData();
        formData.append('file', quoteFile);
        formData.append('type', 'quote_document');
        try {
          await axios.post(`${API_URL}/api/contractor/job/${jobId}/upload?token=${encodeURIComponent(token)}`, formData);
        } catch (uploadErr) {
          console.warn('Quote file upload failed:', uploadErr);
        }
      }
      
      try {
        await api.post(`/contractor-company/jobs/${jobId}/quote`, { 
          amount: total, 
          labour: labourNum,
          materials: materialsNum,
          notes: quoteNotes 
        });
      } catch {
        await axios.post(`${API_URL}/api/contractor/job/${jobId}/quote?token=${encodeURIComponent(token)}`, quoteData);
      }
      
      // Track in Sentry/PostHog
      Sentry.addBreadcrumb({
        category: 'quote',
        message: `Quote submitted: £${total.toFixed(2)}`,
        level: 'info',
      });
      
      toast.success('Quote submitted!');
      setShowQuoteForm(false);
      setQuoteLabour('');
      setQuoteLabourDesc('');
      setQuoteMaterials('');
      setQuoteMaterialsDesc('');
      setQuoteNotes('');
      setQuoteStartDate('');
      setQuoteDuration('');
      setQuoteFile(null);
      loadJob();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'submitQuote' },
        extra: { jobId, total }
      });
      toast.error(err.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setActionLoading(null);
    }
  };

  // Add Note
  const handleAddNote = async () => {
    const note = prompt('Enter your note:');
    if (!note) return;

    setActionLoading('note');
    try {
      try {
        await api.post(`/mobile/jobs/${jobId}/notes`, { content: note, note_type: 'general' });
      } catch {
        await axios.post(`${API_URL}/api/contractor/job/${jobId}/note?token=${encodeURIComponent(token)}`, { note });
      }
      toast.success('Note added');
      loadJob();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setActionLoading(null);
    }
  };

  // Photo Upload
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newPhotos = files.map(file => ({
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      uploaded: false,
      type: 'completion'
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
    toast.success(`${files.length} photo(s) added`);
  };

  // Remove local photo
  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  // Job sheet upload
  const handleJobSheetUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setJobSheet(file);
    toast.success('Job sheet added');
  };

  // Capture signature (simplified)
  const captureSignature = async () => {
    if (!signerName.trim()) {
      toast.error('Please enter signer name');
      return;
    }

    setActionLoading('signature');
    try {
      await api.post(`/mobile/jobs/${jobId}/signature`, {
        signature_data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        signer_name: signerName,
        signer_role: 'customer'
      });
      toast.success('Signature captured');
      setShowSignature(false);
      loadJob();
    } catch (err) {
      toast.error('Failed to capture signature');
    } finally {
      setActionLoading(null);
    }
  };

  // Open in Maps
  const openInMaps = () => {
    const address = job?.address || job?.postcode || `${job?.park_name || job?.site_name} ${job?.plot || job?.unit_name}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  // Call site contact
  const callSite = () => {
    const phone = job?.contact_phone || job?.reporter_phone;
    if (phone) window.location.href = `tel:${phone}`;
    else toast.error('No contact phone available');
  };

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading job...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // ERROR STATE - Recoverable with Try Again
  // ============================================================
  if (loadError || !job) {
    const error = loadError || { type: 'error', message: 'Job Not Found', detail: 'Unable to load this job' };
    return (
      <div className="min-h-screen bg-slate-100 p-4">
        <div className="bg-white rounded-2xl p-6 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            error.type === 'expired' ? 'bg-amber-100' : 'bg-red-100'
          }`}>
            <svg className={`w-8 h-8 ${error.type === 'expired' ? 'text-amber-500' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">{error.message}</h2>
          <p className="text-slate-600 mb-4">{error.detail}</p>
          
          {/* Action buttons */}
          <div className="space-y-3">
            {error.type !== 'expired' && (
              <button 
                onClick={() => loadJob(0)}
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-xl font-semibold active:scale-95 transition-transform"
                data-testid="retry-load-btn"
              >
                Try Again
              </button>
            )}
            
            {/* Contact Helpdesk */}
            <div className="bg-slate-50 rounded-xl p-4 text-left">
              <p className="text-sm font-medium text-slate-700 mb-2">Need help?</p>
              <div className="flex gap-2">
                <a 
                  href={`tel:${HELPDESK.phone}`}
                  className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold text-center"
                >
                  Call Helpdesk
                </a>
                <a 
                  href={`mailto:${HELPDESK.email}?subject=Job Link Issue - ${jobId || 'Unknown'}`}
                  className="flex-1 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold text-center"
                >
                  Email Office
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(job.status);

  return (
    <div className="min-h-screen bg-slate-100 pb-36" data-testid="contractor-mobile-job">
      {/* Hidden file inputs */}
      <input 
        ref={fileInputRef}
        type="file" 
        accept="image/*" 
        multiple 
        hidden 
        onChange={handlePhotoUpload}
      />
      <input 
        ref={jobSheetInputRef}
        type="file" 
        accept=".pdf,.doc,.docx,image/*" 
        hidden 
        onChange={handleJobSheetUpload}
      />

      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-700 text-white sticky top-0 z-50 safe-area-top">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/contractor/mobile')}
              className="p-2 -ml-2 rounded-xl bg-white/10 active:bg-white/20"
              data-testid="back-btn"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">
                {job.job_reference || job.reference || job.id}
              </div>
              <div className="text-sm text-teal-100 truncate">
                {job.park_name || job.site_name}
              </div>
            </div>
            <span className={`${statusConfig.bg} text-white text-xs font-semibold px-3 py-1.5 rounded-full`}>
              {statusConfig.label}
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-4">
        
        {/* Location Card - Large Touch Target */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden">
          <button 
            onClick={openInMaps}
            className="w-full p-4 flex items-center gap-4 active:bg-slate-50 transition-colors text-left"
            data-testid="open-maps-btn"
          >
            <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">
                {job.park_name || job.site_name || 'Site'}
              </div>
              <div className="text-sm text-slate-600 truncate">
                {job.address || `Plot ${job.plot || job.unit_name}`}
              </div>
              <div className="text-sm text-slate-500">
                {job.postcode}
              </div>
            </div>
            <svg className="w-6 h-6 text-teal-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          {/* Contact Row */}
          {(job.contact_name || job.contact_phone) && (
            <div className="border-t border-slate-200 p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-800">
                  {job.contact_name || 'Site Contact'}
                </div>
                <div className="text-sm text-slate-500">
                  {job.contact_phone || job.reporter_phone || 'No phone'}
                </div>
              </div>
              <button 
                onClick={callSite}
                className="p-3 bg-green-500 text-white rounded-xl active:scale-95 transition-transform"
                data-testid="call-site-btn"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Job Details */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Job Details</h3>
          <p className="text-slate-700 text-sm mb-4">
            {job.description || job.title || 'No description'}
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-500">Trade</div>
              <div className="font-medium text-slate-900">{job.trade || 'General'}</div>
            </div>
            <div>
              <div className="text-slate-500">Priority</div>
              <div className={`font-medium ${job.priority === 'Emergency' || job.priority === 'Urgent' ? 'text-red-600' : 'text-slate-900'}`}>
                {job.priority || 'Normal'}
              </div>
            </div>
            {job.scheduled_date && (
              <div>
                <div className="text-slate-500">Scheduled</div>
                <div className="font-medium text-slate-900">
                  {new Date(job.scheduled_date).toLocaleDateString('en-GB')}
                </div>
              </div>
            )}
            {job.po_number && (
              <div>
                <div className="text-slate-500">PO Number</div>
                <div className="font-medium text-emerald-600">{job.po_number}</div>
              </div>
            )}
          </div>
        </div>
        
        {/* Helpdesk Contact - Always visible */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-3">
          <div className="text-xs font-medium text-teal-700 mb-2">Need Help?</div>
          <div className="flex gap-2">
            <a 
              href={`tel:${HELPDESK.phone}`}
              className="flex-1 py-2 bg-teal-600 text-white rounded-xl text-xs font-semibold text-center active:scale-95 transition-transform"
              data-testid="call-helpdesk-btn"
            >
              Call Helpdesk
            </a>
            <a 
              href={`mailto:${HELPDESK.email}`}
              className="flex-1 py-2 bg-white border border-teal-300 text-teal-700 rounded-xl text-xs font-semibold text-center active:scale-95 transition-transform"
              data-testid="email-helpdesk-btn"
            >
              Email Office
            </a>
          </div>
        </div>

        {/* Photo Upload Section - REQUIRED FOR COMPLETION */}
        {['In Progress', 'Approved', 'Scheduled', 'Work Completed'].includes(job.status) && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Photos</h3>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                photos.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
              }`}>
                {photos.length > 0 ? `${photos.length} added` : 'Required'}
              </span>
            </div>
            
            {/* Photo Grid */}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {photos.map(photo => (
                  <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                    <img 
                      src={photo.preview || photo.url} 
                      alt="Photo" 
                      className="w-full h-full object-cover"
                    />
                    {!photo.uploaded && (
                      <button
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    {photo.uploaded && (
                      <div className="absolute top-1 right-1 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 font-medium flex items-center justify-center gap-2 active:bg-slate-50 transition-colors"
              data-testid="upload-photo-btn"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Add Photos
            </button>
          </div>
        )}

        {/* Job Sheet Upload */}
        {['In Progress', 'Work Completed'].includes(job.status) && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Job Sheet</h3>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                jobSheet ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}>
                {jobSheet ? 'Added' : 'Optional'}
              </span>
            </div>
            
            {jobSheet && (
              <div className="flex items-center gap-3 mb-3 p-3 bg-slate-50 rounded-xl">
                <svg className="w-8 h-8 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {typeof jobSheet === 'string' ? 'Job Sheet' : jobSheet.name}
                  </div>
                </div>
                <button
                  onClick={() => setJobSheet(null)}
                  className="p-2 text-red-500 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <button
              onClick={() => jobSheetInputRef.current?.click()}
              className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 font-medium flex items-center justify-center gap-2 active:bg-slate-50 transition-colors"
              data-testid="upload-jobsheet-btn"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {jobSheet ? 'Replace Job Sheet' : 'Upload Job Sheet'}
            </button>
          </div>
        )}

        {/* Completion Notes - REQUIRED FOR COMPLETION */}
        {['In Progress'].includes(job.status) && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Completion Notes</h3>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                completionNote.trim() ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
              }`}>
                Required
              </span>
            </div>
            <textarea
              value={completionNote}
              onChange={(e) => setCompletionNote(e.target.value)}
              placeholder="Describe work completed, materials used, any issues..."
              className="w-full p-3 border-2 border-slate-200 rounded-xl text-sm min-h-[100px] resize-none focus:border-teal-500 focus:ring-0 outline-none transition-colors"
              data-testid="completion-note-input"
            />
          </div>
        )}

        {/* Quote Form - Full breakdown with all fields */}
        {showQuoteForm && (
          <div className="bg-white rounded-2xl border-2 border-amber-200 p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Submit Quote</h3>
            <div className="space-y-3">
              {/* Labour Section */}
              <div className="bg-slate-50 rounded-xl p-3">
                <label className="text-xs font-medium text-slate-700 block mb-2">Labour</label>
                <input
                  type="text"
                  value={quoteLabourDesc}
                  onChange={(e) => setQuoteLabourDesc(e.target.value)}
                  placeholder="Description of labour"
                  className="w-full p-2 border border-slate-200 rounded-lg text-sm mb-2 focus:border-amber-400 outline-none"
                />
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 text-sm">£</span>
                  <input
                    type="number"
                    value={quoteLabour}
                    onChange={(e) => setQuoteLabour(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 p-2 border border-slate-200 rounded-lg text-lg font-semibold focus:border-amber-400 outline-none"
                    data-testid="quote-labour-input"
                  />
                  <span className="text-xs text-slate-500">ex VAT</span>
                </div>
              </div>
              
              {/* Materials Section */}
              <div className="bg-slate-50 rounded-xl p-3">
                <label className="text-xs font-medium text-slate-700 block mb-2">Materials</label>
                <input
                  type="text"
                  value={quoteMaterialsDesc}
                  onChange={(e) => setQuoteMaterialsDesc(e.target.value)}
                  placeholder="List of materials"
                  className="w-full p-2 border border-slate-200 rounded-lg text-sm mb-2 focus:border-amber-400 outline-none"
                />
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 text-sm">£</span>
                  <input
                    type="number"
                    value={quoteMaterials}
                    onChange={(e) => setQuoteMaterials(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 p-2 border border-slate-200 rounded-lg text-lg font-semibold focus:border-amber-400 outline-none"
                    data-testid="quote-materials-input"
                  />
                  <span className="text-xs text-slate-500">ex VAT</span>
                </div>
              </div>
              
              {/* Auto-calculated totals */}
              {subtotal > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal (ex VAT)</span>
                    <span className="font-medium">£{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">VAT @ 20%</span>
                    <span className="font-medium">£{vatAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-1 border-t border-amber-200">
                    <span className="text-amber-700">Total (inc VAT)</span>
                    <span className="text-amber-700">£{total.toFixed(2)}</span>
                  </div>
                </div>
              )}
              
              {/* Timescales */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Est. Start Date</label>
                  <input
                    type="date"
                    value={quoteStartDate}
                    onChange={(e) => setQuoteStartDate(e.target.value)}
                    className="w-full p-2 border border-slate-200 rounded-lg text-sm focus:border-amber-400 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Duration</label>
                  <input
                    type="text"
                    value={quoteDuration}
                    onChange={(e) => setQuoteDuration(e.target.value)}
                    placeholder="e.g. 2 days"
                    className="w-full p-2 border border-slate-200 rounded-lg text-sm focus:border-amber-400 outline-none"
                  />
                </div>
              </div>
              
              {/* Scope/Notes */}
              <div>
                <label className="text-xs text-slate-600 block mb-1">Scope of Works</label>
                <textarea
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Full scope of works, exclusions, terms..."
                  className="w-full p-2 border border-slate-200 rounded-xl text-sm min-h-[80px] resize-none focus:border-amber-400 outline-none"
                />
              </div>
              
              {/* PDF Upload */}
              <div>
                <label className="text-xs text-slate-600 block mb-1">Upload Quote PDF (optional)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setQuoteFile(e.target.files?.[0] || null)}
                  className="w-full p-2 border border-dashed border-slate-300 rounded-xl text-sm file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:bg-amber-100 file:text-amber-700 file:font-medium"
                />
                {quoteFile && (
                  <p className="text-xs text-emerald-600 mt-1">✓ {quoteFile.name}</p>
                )}
              </div>
              
              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowQuoteForm(false)}
                  className="flex-1 py-3 border-2 border-slate-200 rounded-xl font-medium text-slate-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitQuote}
                  disabled={actionLoading === 'quote' || subtotal <= 0}
                  className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-quote-btn"
                >
                  {actionLoading === 'quote' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    'Submit Quote'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Note Button */}
        <button
          onClick={handleAddNote}
          disabled={actionLoading === 'note'}
          className="w-full py-3 bg-slate-100 border-2 border-slate-200 rounded-xl text-slate-700 font-medium flex items-center justify-center gap-2 active:bg-slate-200 transition-colors"
          data-testid="add-note-btn"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Add Note
        </button>
      </main>

      {/* Sticky Bottom Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-200 p-4 safe-area-bottom z-50">
        {job.status === 'Assigned' && (
          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              disabled={actionLoading === 'decline'}
              className="flex-1 py-4 border-2 border-red-200 bg-red-50 text-red-600 rounded-xl font-semibold active:scale-[0.98] transition-transform disabled:opacity-50"
              data-testid="decline-btn"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={actionLoading === 'accept'}
              className="flex-1 py-4 bg-emerald-500 text-white rounded-xl font-semibold active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="accept-btn"
            >
              {actionLoading === 'accept' ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Accepting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Accept Job
                </>
              )}
            </button>
          </div>
        )}

        {job.status === 'Awaiting Quote' && (
          <button
            onClick={() => setShowQuoteForm(true)}
            className="w-full py-4 bg-amber-500 text-white rounded-xl font-semibold active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
            data-testid="quote-btn"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Submit Quote
          </button>
        )}

        {['Scheduled', 'Approved'].includes(job.status) && (
          <button
            onClick={handleStart}
            disabled={actionLoading === 'start'}
            className="w-full py-4 bg-teal-600 text-white rounded-xl font-semibold active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="start-btn"
          >
            {actionLoading === 'start' ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Starting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Start Work
              </>
            )}
          </button>
        )}

        {job.status === 'In Progress' && (
          <button
            onClick={handleComplete}
            disabled={actionLoading === 'complete' || photos.length === 0 || !completionNote.trim()}
            className={`w-full py-4 rounded-xl font-semibold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 ${
              photos.length === 0 || !completionNote.trim()
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-emerald-500 text-white'
            }`}
            data-testid="complete-btn"
          >
            {actionLoading === 'complete' ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Completing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {photos.length === 0 || !completionNote.trim() 
                  ? 'Add Photos & Notes to Complete' 
                  : 'Complete Job'}
              </>
            )}
          </button>
        )}

        {job.status === 'Work Completed' && (
          <div className="text-center py-3">
            <div className="flex items-center justify-center gap-2 text-emerald-600 font-semibold">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Job Completed
            </div>
            <p className="text-sm text-slate-500 mt-1">Awaiting review</p>
          </div>
        )}
      </div>

      {/* Signature Modal */}
      {showSignature && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-3xl p-4 safe-area-bottom">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Capture Signature</h3>
              <button onClick={() => setShowSignature(false)} className="p-2">
                <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Signer name"
              className="w-full p-3 border-2 border-slate-200 rounded-xl mb-4"
            />
            <div className="h-40 border-2 border-slate-200 rounded-xl bg-slate-50 mb-4 flex items-center justify-center text-slate-400">
              Sign here (touch to draw)
            </div>
            <button
              onClick={captureSignature}
              disabled={actionLoading === 'signature'}
              className="w-full py-3 bg-teal-600 text-white rounded-xl font-semibold"
            >
              {actionLoading === 'signature' ? 'Saving...' : 'Save Signature'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
